import { useCallback, useEffect, useMemo, useState } from 'react';
import { differenceInDays, format, parseISO, startOfDay } from 'date-fns';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useApp } from '@/contexts/AppContext';
import AppDateField from '@/components/AppDateField';
import type { Child, FamilyChildcareProviderType } from '@/types';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppEmptyState,
  AppFieldGroup,
  AppInlineAlert,
  AppInput,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from '../../../ui-foundation';
import { AppNativeSelect } from '../../../ui-foundation/adapters/web';

type FamilyChildcareRow = Database['public']['Tables']['family_childcare_records']['Row'];

const PROVIDER_OPTIONS: Array<{ value: FamilyChildcareProviderType; label: string }> = [
  { value: 'nursery', label: 'Nursery' },
  { value: 'childminder', label: 'Childminder' },
  { value: 'after-school-club', label: 'After-school club' },
  { value: 'nanny', label: 'Nanny' },
  { value: 'holiday-club', label: 'Holiday club' },
  { value: 'other', label: 'Other' },
];

function notifySectionRefresh() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function formatMoney(value: number | null) {
  if (value == null) return null;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
}

function formatDate(value: string | null) {
  if (!value) return null;
  return format(parseISO(value), 'd MMM yyyy');
}

function getNextChildcareAttention(record: FamilyChildcareRow) {
  const deadlines = [
    record.contract_end_date ? { label: 'Contract', date: record.contract_end_date } : null,
    record.tax_free_top_up_date ? { label: 'Tax-Free Childcare top-up', date: record.tax_free_top_up_date } : null,
    record.voucher_renewal_date ? { label: 'Voucher renewal', date: record.voucher_renewal_date } : null,
  ].filter(Boolean) as Array<{ label: string; date: string }>;

  return deadlines
    .map((item) => ({
      ...item,
      daysUntil: differenceInDays(parseISO(item.date), startOfDay(new Date())),
    }))
    .sort((left, right) => left.daysUntil - right.daysUntil)[0] || null;
}

function getAttentionBadge(record: FamilyChildcareRow) {
  const attention = getNextChildcareAttention(record);
  if (!attention) return { text: 'No due date saved', classes: 'bg-muted text-muted-foreground' };
  if (attention.daysUntil < 0) {
    return {
      text: `${attention.label} overdue`,
      classes: 'bg-red-100 text-red-800',
    };
  }
  if (attention.daysUntil <= 30) {
    return {
      text: `${attention.label} in ${attention.daysUntil} day${attention.daysUntil === 1 ? '' : 's'}`,
      classes: 'bg-mustard-light text-accent',
    };
  }
  return {
    text: `${attention.label} on ${formatDate(attention.date)}`,
    classes: 'bg-success-light text-success',
  };
}

function getFundedHoursSummary(child: Child | undefined, target: number) {
  if (!child) {
    return target > 0
      ? `Track whether the ${target}-hour entitlement is actually being used.`
      : 'No funded-hours target saved yet.';
  }

  if (child.age <= 1) {
    return 'Standard 15/30 funded hours usually start later, so this is mostly for keeping cost and provider detail together.';
  }

  if (child.age === 2) {
    return target > 0
      ? `${child.name} is 2, so it is worth checking whether funded hours already apply for your circumstances.`
      : `${child.name} is 2, so Adulting can help you remember to check two-year funding if it becomes relevant.`;
  }

  if (child.age >= 3 && child.age <= 4) {
    return target > 0
      ? `${child.name} is in the usual funded-hours window. Keep ${target} hours visible against what the provider is actually billing.`
      : `${child.name} is in the usual funded-hours window. Save 15 or 30 hours here if that support applies.`;
  }

  return target > 0
    ? `${child.name} is ${child.age}, so use this mainly to track wraparound or holiday care against the saved ${target}-hour target.`
    : `${child.name} is ${child.age}, so use this mainly for wraparound, holiday club, or after-school care tracking.`;
}

export default function FamilyChildcareSection() {
  const { user, data } = useApp();
  const [records, setRecords] = useState<FamilyChildcareRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState<FamilyChildcareRow | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [childSelection, setChildSelection] = useState('');
  const [providerName, setProviderName] = useState('');
  const [providerType, setProviderType] = useState<FamilyChildcareProviderType>('nursery');
  const [monthlyCost, setMonthlyCost] = useState('');
  const [hoursPerWeek, setHoursPerWeek] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [taxFreeChildcareEnabled, setTaxFreeChildcareEnabled] = useState(false);
  const [taxFreeChildcareLink, setTaxFreeChildcareLink] = useState('');
  const [taxFreeTopUpDate, setTaxFreeTopUpDate] = useState('');
  const [fundedHoursTarget, setFundedHoursTarget] = useState<0 | 15 | 30>(0);
  const [childcareVouchersEnabled, setChildcareVouchersEnabled] = useState(false);
  const [voucherRenewalDate, setVoucherRenewalDate] = useState('');
  const [notes, setNotes] = useState('');

  const namedChildren = useMemo(
    () => data.profile.children.filter((child) => child.name.trim().length > 0),
    [data.profile.children],
  );

  const fetchRecords = useCallback(async () => {
    if (!user) return;
    const { data: result } = await supabase
      .from('family_childcare_records')
      .select('*')
      .eq('user_id', user.id)
      .order('contract_end_date', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: false });

    setRecords((result as FamilyChildcareRow[]) || []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchRecords();
  }, [fetchRecords]);

  const resetForm = () => {
    setEditingRecord(null);
    setChildSelection(namedChildren[0]?.id || '');
    setProviderName('');
    setProviderType('nursery');
    setMonthlyCost('');
    setHoursPerWeek('');
    setContractEndDate('');
    setTaxFreeChildcareEnabled(false);
    setTaxFreeChildcareLink('');
    setTaxFreeTopUpDate('');
    setFundedHoursTarget(0);
    setChildcareVouchersEnabled(false);
    setVoucherRenewalDate('');
    setNotes('');
    setShowForm(false);
    setConfirmDeleteId(null);
  };

  const openNewRecord = () => {
    setEditingRecord(null);
    setChildSelection(namedChildren[0]?.id || '');
    setProviderName('');
    setProviderType('nursery');
    setMonthlyCost('');
    setHoursPerWeek('');
    setContractEndDate('');
    setTaxFreeChildcareEnabled(false);
    setTaxFreeChildcareLink('');
    setTaxFreeTopUpDate('');
    setFundedHoursTarget(0);
    setChildcareVouchersEnabled(false);
    setVoucherRenewalDate('');
    setNotes('');
    setShowForm(true);
  };

  const openEditRecord = (record: FamilyChildcareRow) => {
    const matchingChild = namedChildren.find((child) => child.id === record.child_id);
    setEditingRecord(record);
    setChildSelection(matchingChild?.id || '__saved__');
    setProviderName(record.provider_name || '');
    setProviderType((record.provider_type as FamilyChildcareProviderType) || 'nursery');
    setMonthlyCost(record.monthly_cost != null ? String(record.monthly_cost) : '');
    setHoursPerWeek(record.hours_per_week != null ? String(record.hours_per_week) : '');
    setContractEndDate(record.contract_end_date || '');
    setTaxFreeChildcareEnabled(record.tax_free_childcare_enabled);
    setTaxFreeChildcareLink(record.tax_free_childcare_link || '');
    setTaxFreeTopUpDate(record.tax_free_top_up_date || '');
    setFundedHoursTarget((record.funded_hours_target as 0 | 15 | 30) || 0);
    setChildcareVouchersEnabled(record.childcare_vouchers_enabled);
    setVoucherRenewalDate(record.voucher_renewal_date || '');
    setNotes(record.notes || '');
    setShowForm(true);
  };

  const selectedChild = namedChildren.find((child) => child.id === childSelection);
  const selectedChildName = selectedChild?.name?.trim()
    || (childSelection === '__saved__' ? editingRecord?.child_name?.trim() || '' : '');
  const selectedChildId = selectedChild?.id || null;
  const selectedChildForSummary = selectedChild || namedChildren.find((child) => child.name.trim() === selectedChildName);
  const canSave = Boolean(selectedChildName && providerName.trim());

  const stats = useMemo(() => {
    const dueSoon = records.filter((record) => {
      const attention = getNextChildcareAttention(record);
      return attention ? attention.daysUntil <= 30 : false;
    }).length;

    return {
      active: records.length,
      dueSoon,
    };
  }, [records]);

  const handleSave = async () => {
    if (!user || !canSave) return;
    setSaving(true);

    const parsedMonthlyCost = monthlyCost.trim() ? Number(monthlyCost) : null;
    const parsedHours = hoursPerWeek.trim() ? Number(hoursPerWeek) : null;

    const payload = {
      user_id: user.id,
      child_id: selectedChildId,
      child_name: selectedChildName,
      provider_name: providerName.trim(),
      provider_type: providerType,
      monthly_cost: Number.isFinite(parsedMonthlyCost) ? parsedMonthlyCost : null,
      hours_per_week: Number.isFinite(parsedHours) ? parsedHours : null,
      contract_end_date: contractEndDate || null,
      tax_free_childcare_enabled: taxFreeChildcareEnabled,
      tax_free_childcare_link: taxFreeChildcareLink.trim() || null,
      tax_free_top_up_date: taxFreeChildcareEnabled && taxFreeTopUpDate ? taxFreeTopUpDate : null,
      funded_hours_target: fundedHoursTarget,
      childcare_vouchers_enabled: childcareVouchersEnabled,
      voucher_renewal_date: childcareVouchersEnabled && voucherRenewalDate ? voucherRenewalDate : null,
      notes: notes.trim() || null,
    };

    const { error } = editingRecord
      ? await supabase.from('family_childcare_records').update(payload).eq('id', editingRecord.id)
      : await supabase.from('family_childcare_records').insert(payload);

    if (error) {
      console.error(error);
      toast.error('Could not save the childcare tracker right now.');
      setSaving(false);
      return;
    }

    toast.success(editingRecord ? 'Childcare tracker updated.' : 'Childcare tracker saved.');
    setSaving(false);
    notifySectionRefresh();
    resetForm();
    void fetchRecords();
  };

  const handleDelete = async (record: FamilyChildcareRow) => {
    const { error } = await supabase.from('family_childcare_records').delete().eq('id', record.id);
    if (error) {
      console.error(error);
      toast.error('Could not remove that childcare record.');
      return;
    }

    toast.success('Childcare record removed.');
    setConfirmDeleteId(null);
    notifySectionRefresh();
    void fetchRecords();
  };

  if (loading) {
    return (
      <AppLoadingState
        title="Loading childcare tracker"
        body="Pulling provider details, costs, funded-hours notes, and top-up timing."
      />
    );
  }

  return (
    <AppCard>
      <AppSectionHeader
        title="Childcare tracker"
        subtitle="Keep provider details, cost, funded-hours context, contract dates, and top-up timing in one place."
        action={(
          <AppButton
            size="sm"
            onPress={() => (showForm ? resetForm() : openNewRecord())}
            disabled={namedChildren.length === 0}
          >
            <Plus size={12} />
            {showForm ? 'Cancel' : 'Add'}
          </AppButton>
        )}
      />

      <div className="grid grid-cols-2 gap-2 mt-4 sm:grid-cols-4">
        <MiniMetric label="Tracked" value={stats.active} />
        <MiniMetric label="Due soon" value={stats.dueSoon} />
      </div>

      {showForm && (
        <AppCard backgroundColor="$mutedBackground" className="mt-4 animate-fade-in">
          {namedChildren.length === 0 ? (
            <AppInlineAlert title="Add a child in Profile first">
              Then come back here to track childcare cost and deadlines.
            </AppInlineAlert>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppFieldGroup label="Child">
                  <AppNativeSelect
                    value={childSelection}
                    onChange={(event) => setChildSelection(event.target.value)}
                  >
                    <option value="">Select child…</option>
                    {editingRecord && childSelection === '__saved__' ? (
                      <option value="__saved__">{editingRecord.child_name} (saved name)</option>
                    ) : null}
                    {namedChildren.map((child) => (
                      <option key={child.id} value={child.id}>
                        {child.name}
                      </option>
                    ))}
                  </AppNativeSelect>
                </AppFieldGroup>
                <AppFieldGroup label="Provider">
                  <AppInput
                    tag="input"
                    value={providerName}
                    onChange={(event) => setProviderName((event.target as HTMLInputElement).value)}
                    placeholder="e.g. Little Oaks Nursery"
                  />
                </AppFieldGroup>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <AppFieldGroup label="Provider type">
                  <AppNativeSelect
                    value={providerType}
                    onChange={(event) => setProviderType(event.target.value as FamilyChildcareProviderType)}
                  >
                    {PROVIDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AppNativeSelect>
                </AppFieldGroup>
                <AppFieldGroup label="Monthly cost (£)">
                  <AppInput
                    tag="input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={monthlyCost}
                    onChange={(event) => setMonthlyCost((event.target as HTMLInputElement).value)}
                    placeholder="e.g. 620"
                  />
                </AppFieldGroup>
                <AppFieldGroup label="Hours per week">
                  <AppInput
                    tag="input"
                    type="number"
                    min="0"
                    step="0.5"
                    value={hoursPerWeek}
                    onChange={(event) => setHoursPerWeek((event.target as HTMLInputElement).value)}
                    placeholder="e.g. 24"
                  />
                </AppFieldGroup>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppDateField label="Contract end date" value={contractEndDate} onChange={setContractEndDate} />
                <AppFieldGroup label="Funded-hours target">
                  <AppNativeSelect
                    value={String(fundedHoursTarget)}
                    onChange={(event) => setFundedHoursTarget(Number(event.target.value) as 0 | 15 | 30)}
                  >
                    <option value="0">Not tracking funded hours yet</option>
                    <option value="15">Track 15 funded hours</option>
                    <option value="30">Track 30 funded hours</option>
                  </AppNativeSelect>
                </AppFieldGroup>
              </div>

              <AppCard backgroundColor="$background">
                <AppText fontSize="$xs" color="$colorMuted">
                  {getFundedHoursSummary(selectedChildForSummary, fundedHoursTarget)}
                </AppText>
              </AppCard>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AppCard backgroundColor="$background">
                  <div className="flex items-center justify-between gap-2">
                    <AppText fontSize="$xs" fontWeight="700">Tax-Free Childcare</AppText>
                    <AppNativeSelect
                      value={taxFreeChildcareEnabled ? 'yes' : 'no'}
                      onChange={(event) => setTaxFreeChildcareEnabled(event.target.value === 'yes')}
                      className="px-2 py-1 text-xs"
                    >
                      <option value="no">Not using</option>
                      <option value="yes">Using it</option>
                    </AppNativeSelect>
                  </div>
                  {taxFreeChildcareEnabled ? (
                    <div className="space-y-2 mt-3">
                      <AppInput
                        tag="input"
                        type="url"
                        value={taxFreeChildcareLink}
                        onChange={(event) => setTaxFreeChildcareLink((event.target as HTMLInputElement).value)}
                        placeholder="Optional account link"
                        backgroundColor="$mutedBackground"
                      />
                      <AppDateField
                        label="Tax-Free Childcare top-up date"
                        value={taxFreeTopUpDate}
                        onChange={setTaxFreeTopUpDate}
                      />
                      <AppText fontSize="$xs" color="$colorMuted">
                        Add the next top-up date so Family can flag it before the deadline lands.
                      </AppText>
                    </div>
                  ) : null}
                </AppCard>

                <AppCard backgroundColor="$background">
                  <div className="flex items-center justify-between gap-2">
                    <AppText fontSize="$xs" fontWeight="700">Childcare vouchers</AppText>
                    <AppNativeSelect
                      value={childcareVouchersEnabled ? 'yes' : 'no'}
                      onChange={(event) => setChildcareVouchersEnabled(event.target.value === 'yes')}
                      className="px-2 py-1 text-xs"
                    >
                      <option value="no">Not using</option>
                      <option value="yes">Using them</option>
                    </AppNativeSelect>
                  </div>
                  {childcareVouchersEnabled ? (
                    <div className="space-y-2 mt-3">
                      <AppDateField
                        label="Voucher renewal date"
                        value={voucherRenewalDate}
                        onChange={setVoucherRenewalDate}
                      />
                      <AppText fontSize="$xs" color="$colorMuted">
                        Save the next renewal or revalidation date if the scheme still needs one.
                      </AppText>
                    </div>
                  ) : null}
                </AppCard>
              </div>

              <AppTextArea
                value={notes}
                onChange={(event) => setNotes((event.target as HTMLTextAreaElement).value)}
                rows={3}
                placeholder="Anything practical you want kept with the record: pickup pattern, funded-hours notes, or who pays what."
              />

              <AppButton onPress={() => void handleSave()} disabled={!canSave || saving}>
                {saving ? 'Saving…' : editingRecord ? 'Update childcare tracker' : 'Save childcare tracker'}
              </AppButton>
            </>
          )}
        </AppCard>
      )}

      <div className="space-y-3 mt-4">
        {records.length === 0 ? (
          <AppEmptyState
            title={namedChildren.length === 0 ? 'Add a child in Profile first' : 'No childcare tracker records yet'}
            body={
              namedChildren.length === 0
                ? 'Then save childcare provider, cost, and deadline detail here.'
                : 'Save the current provider, cost, and next top-up or contract date here.'
            }
          />
        ) : (
          records.map((record) => {
            const badge = getAttentionBadge(record);
            const matchingChild = namedChildren.find((child) => child.id === record.child_id) || namedChildren.find((child) => child.name.trim() === record.child_name);
            const fundedSummary = getFundedHoursSummary(matchingChild, record.funded_hours_target);
            return (
              <AppCard key={record.id} backgroundColor="$background">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <AppText fontSize="$sm" fontWeight="700">{record.provider_name}</AppText>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.classes}`}>
                        {badge.text}
                      </span>
                    </div>
                    <AppText className="mt-1" fontSize="$xs" color="$colorMuted">
                      {record.child_name}
                      {' · '}
                      {PROVIDER_OPTIONS.find((option) => option.value === record.provider_type)?.label || 'Childcare'}
                      {record.monthly_cost != null ? ` · ${formatMoney(record.monthly_cost)}/month` : ''}
                      {record.hours_per_week != null ? ` · ${record.hours_per_week} hrs/week` : ''}
                    </AppText>
                    <div className="space-y-1 mt-2">
                      {record.contract_end_date ? <AppText fontSize="$sm" color="$colorMuted">Contract ends {formatDate(record.contract_end_date)}</AppText> : null}
                      {record.tax_free_childcare_enabled ? (
                        <AppText fontSize="$sm" color="$colorMuted">
                          Tax-Free Childcare
                          {record.tax_free_top_up_date ? ` · next top-up ${formatDate(record.tax_free_top_up_date)}` : ' · top-up date not saved yet'}
                        </AppText>
                      ) : null}
                      {record.childcare_vouchers_enabled ? (
                        <AppText fontSize="$sm" color="$colorMuted">
                          Childcare vouchers
                          {record.voucher_renewal_date ? ` · renewal ${formatDate(record.voucher_renewal_date)}` : ' · renewal date not saved yet'}
                        </AppText>
                      ) : null}
                      <AppText fontSize="$sm" color="$colorMuted">{fundedSummary}</AppText>
                      {record.notes ? <AppText fontSize="$sm" color="$colorMuted">{record.notes}</AppText> : null}
                    </div>
                  </div>
                </div>
                <AppButtonRow mt="$sm">
                  {record.tax_free_childcare_link ? (
                    <AppButton
                      size="sm"
                      tone="outline"
                      onPress={() => window.open(record.tax_free_childcare_link || '', '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink size={12} />
                      Open account
                    </AppButton>
                  ) : null}
                  <AppButton size="sm" tone="outline" onPress={() => openEditRecord(record)}>
                    <Pencil size={12} />
                    Edit
                  </AppButton>
                  {confirmDeleteId === record.id ? (
                    <>
                      <AppButton size="sm" tone="destructive" onPress={() => void handleDelete(record)}>
                        Confirm delete
                      </AppButton>
                      <AppButton size="sm" tone="outline" onPress={() => setConfirmDeleteId(null)}>
                        Cancel
                      </AppButton>
                    </>
                  ) : (
                    <AppButton size="sm" tone="outline" onPress={() => setConfirmDeleteId(record.id)}>
                      <Trash2 size={12} />
                      Delete
                    </AppButton>
                  )}
                </AppButtonRow>
              </AppCard>
            );
          })
        )}
      </div>
    </AppCard>
  );
}

function MiniMetric({ label, value }: { label: string; value: number }) {
  return (
    <AppCard px="$sm" py="$sm" backgroundColor="$background">
      <AppText fontSize="$xs" fontWeight="700" textTransform="uppercase" letterSpacing={0.8} color="$colorMuted">
        {label}
      </AppText>
      <AppText className="mt-1" fontSize="$xl" fontWeight="700">
        {value}
      </AppText>
    </AppCard>
  );
}
